import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';

import { Box, Typography } from '@mui/material';
import { useTheme } from '@mui/material/styles';

import Loader from '../../../../components/layout/loader/Loader';
import { useAuth } from '../../../../context/AuthContext';
import { useNavigationStack } from '../../../../context/NavigationStackContext';
import { useFirebaseAuth } from '../../../../hooks/useFirebaseAuth';
import { auth } from '../../../../shared/firebase/firebase-client';
import { Paths } from '../../../../shared/paths';
import { runEmailVerificationOnce } from '../../../../utils/auth/email-verification-once';
import { useStyles } from './Verification.styles';

type VerificationOutcome = 'verified_logged_in' | 'verified' | 'error';

export default function Verification() {
  const theme = useTheme();
  const { classes } = useStyles(theme);
  const [searchParams] = useSearchParams();
  const oobCode = searchParams.get('oobCode') ?? '';
  const { checkActionCode, applyActionCode, verifyApi } = useFirebaseAuth();
  const { refreshLoginUserStatus } = useAuth();
  const { navigate } = useNavigationStack();
  const { t } = useTranslation();
  const [errorMessage, setErrorMessage] = useState('');
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    let redirectTimer: ReturnType<typeof setTimeout> | undefined;

    if (!oobCode) {
      setErrorMessage(t('verification.error'));
      setIsLoading(false);
      return;
    }

    const verify = async (): Promise<VerificationOutcome> => {
      let checkedEmail: string | undefined;
      let applySucceeded = false;

      try {
        checkedEmail = await checkActionCode(oobCode);
      } catch (error) {
        console.warn('checkActionCode failed', error);
      }

      try {
        await applyActionCode(oobCode);
        applySucceeded = true;
      } catch (error) {
        console.warn('applyActionCode failed', error);
      }

      if (!applySucceeded) {
        return 'error';
      }

      try {
        await auth.currentUser?.reload();
      } catch (error) {
        console.warn('currentUser reload failed', error);
      }

      const email = auth.currentUser?.email ?? checkedEmail;
      if (email) {
        try {
          await verifyApi(email);
        } catch (error) {
          console.warn('verifyApi failed', error);
        }
      }

      if (auth.currentUser?.emailVerified) {
        try {
          await refreshLoginUserStatus();
          return 'verified_logged_in';
        } catch (error) {
          console.warn('refreshLoginUserStatus failed', error);
          return 'error';
        }
      }

      return 'verified';
    };

    void runEmailVerificationOnce(oobCode, verify).then((outcome) => {
      if (cancelled) {
        return;
      }

      if (outcome === 'verified_logged_in') {
        return;
      }

      if (outcome === 'verified') {
        setErrorMessage(t('verification.success'));
        redirectTimer = setTimeout(() => {
          navigate(Paths.signIn, { replace: true });
        }, 3000);
      } else {
        setErrorMessage(t('verification.error'));
      }

      setIsLoading(false);
    });

    return () => {
      cancelled = true;
      if (redirectTimer) {
        clearTimeout(redirectTimer);
      }
    };
  }, [oobCode]);

  if (isLoading) {
    return (
      <Box className={classes.page}>
        <Loader />
      </Box>
    );
  }

  return (
    <Box className={classes.page}>
      <Box className={classes.appContainer}>
        <Typography variant="h3">{errorMessage}</Typography>
      </Box>
    </Box>
  );
}
